import { useMemo, useState } from "react";

import type {
  Device,
  RibbonOutcome,
  ScanFlow,
  ScanFlowRibbon,
  ScanFlowScenario,
} from "@/api/client";

const OUTCOME_COLOR: Record<RibbonOutcome, string> = {
  caught: "#84CC16",
  finding_low: "#22D3EE",
  finding_medium: "#F59E0B",
  finding_high: "#F43F5E",
};

const OUTCOME_GLOW_ID: Record<RibbonOutcome, string> = {
  caught: "g-ok",
  finding_low: "g-cy",
  finding_medium: "g-wa",
  finding_high: "g-cr",
};

interface SankeyDiagramProps {
  flow: ScanFlow;
  devices: Device[];
  onSelectRibbon?: (ribbon: ScanFlowRibbon) => void;
}

const W = 880;
const H = 460;
const PAD_Y = 24;
const NODE_W = 10;
const COL_X_LEFT = 200;
const COL_X_RIGHT = W - 200;

export function SankeyDiagram({
  flow,
  devices,
  onSelectRibbon,
}: SankeyDiagramProps): JSX.Element {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // Layout: vertical stacking on each side, height proportional to total attempts touching that node.
  const layout = useMemo(() => {
    const scenarioById = new Map<string, ScanFlowScenario>(
      flow.scenarios.map((s) => [s.id, s]),
    );
    const deviceById = new Map<string, Device>(devices.map((d) => [d.id, d]));

    // Per-side totals.
    const scenarioTotals = new Map<string, number>();
    const deviceTotals = new Map<string, number>();
    for (const r of flow.ribbons) {
      scenarioTotals.set(
        r.scenario,
        (scenarioTotals.get(r.scenario) ?? 0) + r.attempts,
      );
      deviceTotals.set(r.device, (deviceTotals.get(r.device) ?? 0) + r.attempts);
    }

    // Active scenarios/devices in display order, biggest at top.
    const scenarios = [...scenarioTotals.keys()]
      .filter((id) => scenarioById.has(id))
      .sort(
        (a, b) => (scenarioTotals.get(b) ?? 0) - (scenarioTotals.get(a) ?? 0),
      );
    const devs = [...deviceTotals.keys()]
      .filter((id) => deviceById.has(id))
      .sort((a, b) => (deviceTotals.get(b) ?? 0) - (deviceTotals.get(a) ?? 0));

    const sumLeft = [...scenarioTotals.values()].reduce((a, b) => a + b, 0);
    const sumRight = [...deviceTotals.values()].reduce((a, b) => a + b, 0);

    const usableH = H - PAD_Y * 2 - 6 * scenarios.length; // 6px gap between nodes
    const unitL = sumLeft > 0 ? usableH / sumLeft : 0;
    const usableHR = H - PAD_Y * 2 - 6 * devs.length;
    const unitR = sumRight > 0 ? usableHR / sumRight : 0;

    type NodeLayout = {
      id: string;
      label: string;
      y: number;
      h: number;
      meta?: string;
    };

    const leftNodes: NodeLayout[] = [];
    let cy = PAD_Y;
    for (const id of scenarios) {
      const total = scenarioTotals.get(id) ?? 0;
      const h = Math.max(8, total * unitL);
      leftNodes.push({
        id,
        label: scenarioById.get(id)?.name ?? id,
        y: cy,
        h,
        meta: `${total} attempts`,
      });
      cy += h + 6;
    }

    const rightNodes: NodeLayout[] = [];
    cy = PAD_Y;
    for (const id of devs) {
      const total = deviceTotals.get(id) ?? 0;
      const h = Math.max(8, total * unitR);
      rightNodes.push({
        id,
        label: deviceById.get(id)?.display_name ?? id,
        y: cy,
        h,
        meta: `${total} attempts`,
      });
      cy += h + 6;
    }

    // Per-node cursor for stacking ribbons.
    const leftCursor = new Map<string, number>(
      leftNodes.map((n) => [n.id, n.y]),
    );
    const rightCursor = new Map<string, number>(
      rightNodes.map((n) => [n.id, n.y]),
    );

    // Stable ribbon order: by scenario index then attempts desc, deterministic.
    const orderedRibbons = [...flow.ribbons].sort((a, b) => {
      const sa = scenarios.indexOf(a.scenario);
      const sb = scenarios.indexOf(b.scenario);
      if (sa !== sb) return sa - sb;
      return b.attempts - a.attempts;
    });

    const ribbons = orderedRibbons.map((r) => {
      const lh = r.attempts * unitL;
      const rh = r.attempts * unitR;
      const ly = leftCursor.get(r.scenario) ?? 0;
      const ry = rightCursor.get(r.device) ?? 0;
      leftCursor.set(r.scenario, ly + lh);
      rightCursor.set(r.device, ry + rh);
      return {
        ribbon: r,
        ly,
        ry,
        lh,
        rh,
      };
    });

    return { leftNodes, rightNodes, ribbons };
  }, [flow, devices]);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full"
      role="img"
      aria-label="Scan flow: scenarios mapped to devices"
    >
      <defs>
        {(["caught", "finding_low", "finding_medium", "finding_high"] as RibbonOutcome[]).map(
          (oc) => (
            <filter
              key={oc}
              id={OUTCOME_GLOW_ID[oc]}
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
            >
              <feGaussianBlur stdDeviation="3" />
            </filter>
          ),
        )}
      </defs>

      {/* Column headings */}
      <text
        x={COL_X_LEFT}
        y={14}
        className="fill-chrome-400"
        textAnchor="end"
        style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase" }}
      >
        scenarios run
      </text>
      <text
        x={COL_X_RIGHT + NODE_W}
        y={14}
        className="fill-chrome-400"
        textAnchor="start"
        style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase" }}
      >
        your devices
      </text>

      {/* Ribbons */}
      <g>
        {layout.ribbons.map(({ ribbon, ly, ry, lh, rh }, i) => {
          const x0 = COL_X_LEFT;
          const x1 = COL_X_RIGHT;
          const cx = (x0 + x1) / 2;
          const yTopL = ly;
          const yBotL = ly + lh;
          const yTopR = ry;
          const yBotR = ry + rh;
          const path = `
            M ${x0} ${yTopL}
            C ${cx} ${yTopL}, ${cx} ${yTopR}, ${x1} ${yTopR}
            L ${x1} ${yBotR}
            C ${cx} ${yBotR}, ${cx} ${yBotL}, ${x0} ${yBotL}
            Z
          `;
          const color = OUTCOME_COLOR[ribbon.outcome];
          const isHover = hoverIdx === i;
          return (
            <path
              key={i}
              d={path}
              fill={color}
              fillOpacity={isHover ? 0.7 : 0.32}
              stroke={color}
              strokeOpacity={isHover ? 0.8 : 0}
              strokeWidth={1}
              style={{ cursor: onSelectRibbon ? "pointer" : "default" }}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
              onClick={() => onSelectRibbon?.(ribbon)}
            >
              <title>{`${ribbon.scenario} → ${ribbon.device} · ${ribbon.attempts} attempts · ${ribbon.outcome.replace("_", " ")}`}</title>
            </path>
          );
        })}
      </g>

      {/* Left nodes */}
      <g>
        {layout.leftNodes.map((n) => (
          <g key={n.id}>
            <rect
              x={COL_X_LEFT - NODE_W}
              y={n.y}
              width={NODE_W}
              height={n.h}
              fill="#22D3EE"
              fillOpacity={0.8}
              rx={1}
            />
            <text
              x={COL_X_LEFT - NODE_W - 10}
              y={n.y + n.h / 2 - 4}
              textAnchor="end"
              className="fill-chrome-100"
              style={{ fontSize: 12 }}
            >
              {n.label}
            </text>
            <text
              x={COL_X_LEFT - NODE_W - 10}
              y={n.y + n.h / 2 + 10}
              textAnchor="end"
              className="fill-chrome-400"
              style={{ fontSize: 10 }}
            >
              {n.meta}
            </text>
          </g>
        ))}
      </g>

      {/* Right nodes */}
      <g>
        {layout.rightNodes.map((n) => (
          <g key={n.id}>
            <rect
              x={COL_X_RIGHT}
              y={n.y}
              width={NODE_W}
              height={n.h}
              fill="#94A3B8"
              fillOpacity={0.8}
              rx={1}
            />
            <text
              x={COL_X_RIGHT + NODE_W + 10}
              y={n.y + n.h / 2 - 4}
              textAnchor="start"
              className="fill-chrome-100"
              style={{ fontSize: 12 }}
            >
              {n.label}
            </text>
            <text
              x={COL_X_RIGHT + NODE_W + 10}
              y={n.y + n.h / 2 + 10}
              textAnchor="start"
              className="fill-chrome-400"
              style={{ fontSize: 10 }}
            >
              {n.meta}
            </text>
          </g>
        ))}
      </g>
    </svg>
  );
}

export const SANKEY_OUTCOME_COLOR = OUTCOME_COLOR;
